/*
 * Where the app is running and how it reaches the API.
 *  - The website (app.tikkas.com, staging) talks to /api on its own origin with the session cookie, as before.
 *  - The iPhone app (VITE_TARGET=app, built by scripts/native/build-web.mjs) runs from capacitor://localhost, so it calls
 *    the API by its full address and signs in with a session token kept in the iOS Keychain, sent as a bearer token.
 *    Staging builds also carry the staging app key, which the staging password gate accepts in place of the password.
 */
import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

/** True for the files bundled inside the iPhone/Android app (fixed at build time). */
export const APP_BUILD = import.meta.env.VITE_TARGET === 'app';
/** 'staging' (TestFlight) or 'production' (App Store) for an app build; 'web' for the website. */
export const APP_ENV: 'staging' | 'production' | 'web' = APP_BUILD ? (import.meta.env.VITE_APP_ENV === 'production' ? 'production' : 'staging') : 'web';
/** Running inside the native shell (not a browser). */
export const NATIVE = Capacitor.isNativePlatform();
export const PLATFORM = Capacitor.getPlatform() as 'ios' | 'android' | 'web';

const API_BASE: string = APP_BUILD ? String(import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '') : '';
const APP_KEY: string = APP_BUILD ? String(import.meta.env.VITE_APP_KEY ?? '') : '';

/** The public website, for links people share (refer-a-friend, team links) and Stripe return pages. */
export const WEB_URL: string = APP_BUILD ? String(import.meta.env.VITE_WEB_URL ?? '').replace(/\/$/, '') : location.origin;

/** Full address of an API path ("/api/…"): same origin on the web, the API's own address in the app. */
export function apiUrl(path: string): string {
  return API_BASE && path.startsWith('/') ? `${API_BASE}${path}` : path;
}

/* ---------- the app's session token (Keychain) ---------- */

const TOKEN_KEY = 'tikkas.session';
let token: string | null = null;
let loaded: Promise<void> | null = null;

/** Read the saved session once at start-up (before the first API call). */
export function loadSessionToken(): Promise<void> {
  if (!APP_BUILD) return Promise.resolve();
  loaded ??= SecureStoragePlugin.get({ key: TOKEN_KEY })
    .then((r) => {
      token = r.value || null;
    })
    .catch(() => {
      token = null; // nothing saved yet
    });
  return loaded;
}

export async function setSessionToken(t: string | null): Promise<void> {
  if (!APP_BUILD) return;
  token = t;
  try {
    if (t) await SecureStoragePlugin.set({ key: TOKEN_KEY, value: t });
    else await SecureStoragePlugin.remove({ key: TOKEN_KEY });
  } catch {
    /* nothing saved to remove */
  }
}

export function hasSessionToken(): boolean {
  return !!token;
}

/** Headers every API call from the app carries. Empty on the website. */
export function clientHeaders(): Record<string, string> {
  if (!APP_BUILD) return {};
  return {
    'x-tikkas-client': PLATFORM === 'web' ? 'app-web' : PLATFORM,
    ...(APP_KEY ? { 'x-tikkas-app-key': APP_KEY } : {}),
    ...(token ? { authorization: `Bearer ${token}` } : {}),
  };
}

/** Staging-only extras (banners, the kitchen simulator, test credit) never show in an app build. */
export function testToolsAllowed(serverSays: boolean | undefined): boolean {
  return !APP_BUILD && !!serverSays;
}
